import * as Sentry from '@sentry/react';
import { useCallback } from 'react';
import {
  AutoResponseSuggestionStatus,
  useCreateChannelMessageAutoResponseMutation,
  useUpdateChannelMessageAutoResponseMutation,
} from 'shared/types/graphql';

type UseQuickReplyTrackingServicesReturnType = {
  createAutoResponseMessage: ({ text }: { text: string }) => Promise<string | undefined | false>;
  updateAutoResponseMessage: ({
    suggestionId,
    channelMessageId,
    suggestionStatus,
  }: {
    suggestionId: string;
    channelMessageId?: string | undefined;
    suggestionStatus: AutoResponseSuggestionStatus;
  }) => Promise<string | undefined | false>;
};

type CreateParams = { text: string };

type UpdateParams = {
  suggestionId: string;
  channelMessageId?: string;
  suggestionStatus: AutoResponseSuggestionStatus;
};

// Abstract try catch, graphql logic and Sentry log
const useQuickReplyUsageTrackingServices = (): UseQuickReplyTrackingServicesReturnType => {
  const [_createAutoResponseMessage] = useCreateChannelMessageAutoResponseMutation();
  const [_updateAutoResponseMessage] = useUpdateChannelMessageAutoResponseMutation();

  const createAutoResponseMessage = useCallback(
    async ({ text }: CreateParams): Promise<string | undefined | false> => {
      try {
        const result = await _createAutoResponseMessage({
          variables: { data: { aiSuggestionMessage: text, suggestionStatus: AutoResponseSuggestionStatus.Generated } },
        });

        return result.data?.createOneChannelMessageAutoResponse.id;
      } catch (error) {
        Sentry.captureException(error);
        return false;
      }
    },
    [_createAutoResponseMessage],
  );

  const updateAutoResponseMessage = useCallback(
    async ({ suggestionId, channelMessageId, suggestionStatus }: UpdateParams): Promise<string | undefined | false> => {
      try {
        const result = await _updateAutoResponseMessage({
          variables: {
            data: {
              suggestionStatus,
              ...(channelMessageId ? { channelMessage: { connect: { id: channelMessageId } } } : {}),
            },
            where: { id: suggestionId },
          },
        });

        return result.data?.updateOneChannelMessageAutoResponse.id;
      } catch (error) {
        Sentry.captureException(error);
        return false;
      }
    },
    [_updateAutoResponseMessage],
  );

  return {
    createAutoResponseMessage,
    updateAutoResponseMessage,
  };
};

export default useQuickReplyUsageTrackingServices;
